import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoBannerCst from "@components/InfoBannerCst";
import { useParams } from "react-router-dom";
import { ListFilter, Search } from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { WizardStepProps } from "@components/WizardCst/types";
import { UserWizardData } from "../types";
import { StepContainer } from "../components/StepLayout";

const PAGE_SIZE = 20;

const AddToGroupsStep: React.FC<WizardStepProps<UserWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("user-wizard-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<FiltersBarValue>({});
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const listUrl = corporateId
    ? `${ServicesURL.staticGroups}/static?id_corporate=${corporateId}`
    : null;

  const hasActiveFilters = useMemo(
    () =>
      Object.values(fieldFilters || {}).some((val) => {
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [fieldFilters],
  );

  const { items, totalItems, totalPages, isLoading } = usePaginatedList(
    listUrl,
    {
      page,
      pageSize,
      search: search || undefined,
      fieldFilters,
    },
  );

  const selected = useMemo(
    () => new Set(data.staticGroupIds),
    [data.staticGroupIds],
  );

  const toggle = (groupId: string | number, next: boolean) => {
    setData((prev) => {
      const set = new Set(prev.staticGroupIds);
      if (next) set.add(groupId);
      else set.delete(groupId);
      return { staticGroupIds: Array.from(set) };
    });
  };

  return (
    <StepContainer>
      <InfoBannerCst>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 700, color: "inherit" }}
        >
          {t("groups.noticeTitle")}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "inherit" }}>
          {t("groups.noticeText")}
        </Typography>
      </InfoBannerCst>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 240,
              "& .MuiFormControl-root": {
                minWidth: "0 !important",
                width: "100% !important",
              },
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              "& .MuiFormHelperText-root": { display: "none" },
            }}
          >
            <InputCst
              id="user-groups-search"
              placeholder={t("groups.search")}
              inputSearch
              value={search}
              onChange={(e: any) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              startAdornment={
                <Search
                  size={16}
                  color={theme.palette.systemSecondary01}
                  style={{ marginRight: 8 }}
                />
              }
            />
          </Box>

          <ButtonSecondary
            size="md"
            endIcon={<ListFilter size={16} />}
            onClick={() => setFiltersPanelOpen((prev) => !prev)}
            sxButton={
              hasActiveFilters || filtersPanelOpen
                ? {
                    flexShrink: 0,
                    border: `2px solid ${theme.palette.systemSecondary01} !important`,
                  }
                : { flexShrink: 0 }
            }
          >
            {t("groups.filtersButton")}
          </ButtonSecondary>
        </Box>

        <FiltersAccordionCst
          open={filtersPanelOpen}
          onClose={() => setFiltersPanelOpen(false)}
          pageId={PagesID["corporate.groups"]}
          value={fieldFilters}
          onApply={(next) => {
            setFieldFilters(next);
            setPage(1);
          }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            color: theme.palette.text.primary,
          }}
        >
          {t("groups.empty")}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((group: any) => (
            <ListItemCardCst
              key={group.id}
              title={group.name || "-"}
              subtitle={{ id: group.id }}
              badges={[]}
              actions={[]}
              selectable
              selected={selected.has(group.id)}
              onSelect={(next) => toggle(group.id, next)}
            />
          ))}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </Box>
      )}
    </StepContainer>
  );
};

export default AddToGroupsStep;
